import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Check, Save, Loader2, Search, Sparkles } from "lucide-react";
import { htmlToExcerpt } from "../../lib/utils";

/**
 * Editorial-review workspace for AI-generated translations.
 * Lists every (record × translatable-field) pair across collections and shows
 * the Spanish source next to the target-language draft, with inline save so
 * the reviewer can polish nuances without opening the full CMS editor.
 */

const COLLECTIONS = [
  { key: "hero_slides", label: "Hero slides", apiList: "/admin/hero-slides", apiPatch: "/admin/hero-slides",
    fields: ["title", "subtitle", "cta_label"], titleOf: (r) => r.title_es || r.id },
  { key: "events", label: "Actividades", apiList: "/admin/events", apiPatch: "/admin/events",
    fields: ["title", "description"], titleOf: (r) => r.title_es || r.slug },
  { key: "news", label: "Noticias", apiList: "/admin/news", apiPatch: "/admin/news",
    fields: ["title", "excerpt", "body"], titleOf: (r) => r.title_es || r.slug },
  { key: "pages", label: "Páginas", apiList: "/admin/pages", apiPatch: "/admin/pages",
    fields: ["title", "subtitle", "body"], titleOf: (r) => r.title_es || r.slug },
  { key: "members", label: "Socios", apiList: "/admin/members", apiPatch: "/admin/members",
    fields: ["description"], titleOf: (r) => r.name },
];
const LANGS = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "ar", label: "العربية" },
];

const isLong = (v) => (v || "").length > 240 || /<(p|h[1-6]|ul|ol)\b/i.test(v || "");

export default function TranslationReviewAdmin() {
  const [lang, setLang] = useState("en");
  const [collectionKey, setCollectionKey] = useState("events");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [drafts, setDrafts] = useState({});   // record.id → partial patch
  const [saving, setSaving] = useState(new Set());
  const [aiEnabled, setAiEnabled] = useState(false);
  const [translating, setTranslating] = useState(new Set());

  const coll = COLLECTIONS.find((c) => c.key === collectionKey);

  useEffect(() => {
    api.get("/settings").then((r) => setAiEnabled(!!r.data.ai_translate_enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(coll.apiList)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    setDrafts({});
  }, [coll]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((it) => {
      if (!needle) return true;
      return coll.titleOf(it).toLowerCase().includes(needle) ||
             (it.slug || "").toLowerCase().includes(needle);
    });
  }, [items, q, coll]);

  const setDraft = (id, field, value) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const saveRow = async (row) => {
    const patch = drafts[row.id];
    if (!patch || Object.keys(patch).length === 0) return;
    setSaving((prev) => new Set(prev).add(row.id));
    try {
      const updated = await api.patch(`${coll.apiPatch}/${row.id}`, patch);
      setItems((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...(updated.data || {}) } : r)));
      setDrafts((prev) => { const n = { ...prev }; delete n[row.id]; return n; });
    } catch (e) {
      alert("Error al guardar: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving((prev) => { const n = new Set(prev); n.delete(row.id); return n; });
    }
  };

  const retranslate = async (row, field) => {
    const source = (row[`${field}_es`] || "").trim();
    if (!source) return;
    const key = `${row.id}:${field}`;
    setTranslating((prev) => new Set(prev).add(key));
    try {
      const { data } = await api.post("/admin/translate", {
        text: source, source_lang: "es", target_lang: lang,
      });
      setDraft(row.id, `${field}_${lang}`, data.translated);
    } catch (e) {
      alert("Fallo IA: " + (e.response?.data?.detail || e.message));
    } finally {
      setTranslating((prev) => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  const currentValue = (row, field) => {
    const key = `${field}_${lang}`;
    return drafts[row.id]?.[key] !== undefined ? drafts[row.id][key] : (row[key] || "");
  };

  return (
    <div data-testid="cms-review">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-[#0F2040]">Revisión editorial</h1>
        <p className="text-sm text-gray-600">
          Compare la fuente en español con la traducción generada por IA y ajuste los matices sin abrir el editor completo.
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 grid md:grid-cols-3 gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Colección</label>
          <select
            value={collectionKey}
            onChange={(e) => setCollectionKey(e.target.value)}
            data-testid="review-collection"
            className="w-full px-3 py-2 border border-gray-200 text-sm bg-white"
          >
            {COLLECTIONS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Idioma a revisar</label>
          <div className="flex gap-1" data-testid="review-lang">
            {LANGS.map((L) => (
              <button
                key={L.code}
                type="button"
                onClick={() => setLang(L.code)}
                className={`flex-1 px-3 py-2 text-sm border transition-colors ${
                  lang === L.code
                    ? "border-[#C8102E] bg-[#FCEAED] text-[#C8102E] font-medium"
                    : "border-gray-200 hover:border-[#0F2040]"
                }`}
                data-testid={`review-lang-${L.code}`}
              >
                {L.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Buscar</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filtrar por título o slug…"
              data-testid="review-search"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 text-sm"
            />
          </div>
        </div>
      </div>

      {loading && <p className="text-sm text-gray-400 py-8 text-center"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Cargando…</p>}
      {!loading && rows.length === 0 && (
        <p className="text-sm text-gray-400 py-16 text-center">Sin registros en esta colección.</p>
      )}

      <div className="space-y-4">
        {rows.map((row) => {
          const dirty = drafts[row.id] && Object.keys(drafts[row.id]).length > 0;
          const isSaving = saving.has(row.id);
          return (
            <div
              key={row.id}
              dir="ltr"
              className={`border p-4 md:p-5 bg-white transition-colors ${dirty ? "border-[#C8102E]" : "border-gray-200"}`}
              data-testid={`review-row-${row.id}`}
            >
              <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium text-[#0F2040] truncate">{coll.titleOf(row)}</h3>
                  <p className="text-xs text-gray-500 truncate">{row.slug || row.id}</p>
                </div>
                {dirty && (
                  <button
                    type="button"
                    onClick={() => saveRow(row)}
                    disabled={isSaving}
                    data-testid={`review-save-${row.id}`}
                    className="inline-flex items-center gap-1.5 text-xs bg-[#0F2040] text-white px-3 py-1.5 hover:bg-[#1A2F5C] disabled:opacity-60"
                  >
                    {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Guardar cambios
                  </button>
                )}
              </div>

              <div className="space-y-3">
                {coll.fields.map((f) => {
                  const es = row[`${f}_es`] || "";
                  if (!es.trim()) return null;
                  const val = currentValue(row, f);
                  const long = isLong(es) || isLong(val);
                  const tKey = `${row.id}:${f}`;
                  return (
                    <div key={f} className="grid md:grid-cols-2 gap-3 items-start">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">ES · {f}</div>
                        {long ? (
                          <div className="text-xs text-gray-700 bg-gray-50 p-3 max-h-40 overflow-y-auto border border-gray-100 leading-relaxed">
                            {htmlToExcerpt(es, 800)}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-700 bg-gray-50 p-3 border border-gray-100">{es}</div>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] uppercase tracking-widest text-[#C8102E]">{lang.toUpperCase()} · {f}</span>
                          {aiEnabled && (
                            <button
                              type="button"
                              onClick={() => retranslate(row, f)}
                              disabled={translating.has(tKey)}
                              data-testid={`review-retranslate-${row.id}-${f}`}
                              className="text-[10px] text-[#C8102E] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                              title={`Re-traducir este campo desde ES a ${lang.toUpperCase()}`}
                            >
                              {translating.has(tKey) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                              Re-traducir
                            </button>
                          )}
                        </div>
                        <textarea
                          value={val}
                          onChange={(e) => setDraft(row.id, `${f}_${lang}`, e.target.value)}
                          rows={long ? 8 : 2}
                          dir={lang === "ar" ? "rtl" : "ltr"}
                          data-testid={`review-field-${row.id}-${f}`}
                          className={`w-full px-3 py-2 border text-sm outline-none focus:border-[#0F2040] ${
                            drafts[row.id]?.[`${f}_${lang}`] !== undefined ? "border-[#C8102E]" : "border-gray-200"
                          }`}
                          placeholder={`Traducción en ${lang.toUpperCase()}…`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
